import { FC, FormEvent, useEffect, useMemo, useState } from 'react';
import { Page, Pagebar, PagebarTitle } from '@monorepo/base/src/components/page/page';
import { Card } from '@monorepo/base/src/components/card/card';
import { Input } from '@monorepo/base/src/components/form/input/input';
import { Dropdown } from '@monorepo/base/src/components/dropdown/dropdown';
import { PrimaryButton } from '@monorepo/base/src/components/buttons/buttons';
import { getConfig } from '@monorepo/tools/src/lib/get-config';
import { CampaignsApi } from '../../modules/apis/campaigns.api';
import { CreativesApi } from '../../modules/apis/creatives.api';
import { CreativeModel } from '../../modules/models/creative.model';
import styles from './visualizer.module.scss';
import { CampaignDestinationLinkModel, CampaignModel, ICampaignCreativeLink } from '../../modules/models/campaign.model';

type CreativeMetaStatus = 'hidden' | 'loading' | 'not_found' | 'resolved';

const PREVIEW_PATH = 'creativePreview';
const CAMPAIGNS_LOAD_RETRY_DELAY_MS = 5000;

function parseIdFromLabel(label: string): string | null {
	const idPrefix = label.match(/^(\d+)\s*-\s*/);
	if (idPrefix) {
		return idPrefix[1];
	}
	const inParens = label.match(/\((\d+)\)\s*$/);
	return inParens ? inParens[1] : null;
}

function firstFromSet(val: Set<string> | undefined): string | undefined {
	if (!val || val.size === 0) {
		return undefined;
	}
	return [...val][0];
}

function buildPreviewUrl(
	baseUrl: string,
	params: { campaignId: string; creativeId: string; width: string; height: string; campaignDestUrlId: string }
): string {
	const trimmedBase = baseUrl.replace(/\/+$/, '');
	const search = new URLSearchParams();
	search.set('campaignId', params.campaignId.trim());
	const creative = params.creativeId.trim();
	const campaignDest = params.campaignDestUrlId.trim();
	if (campaignDest) {
		search.set('destinationId', campaignDest);
	}
	if (creative) {
		search.set('creativeId', creative);
	}
	const w = params.width.trim();
	const h = params.height.trim();
	if (w) {
		search.set('width', w);
	}
	if (h) {
		search.set('height', h);
	}
	return `${trimmedBase}/${PREVIEW_PATH}?${search.toString()}`;
}

function readOnlyFieldValue(status: CreativeMetaStatus, value: string, notFoundFallback = '—'): string {
	if (status === 'loading') {
		return 'Loading…';
	}
	if (status === 'not_found') {
		return notFoundFallback;
	}
	return value || '—';
}

export const VisualizerPage: FC = () => {
	const previewBase = useMemo(() => getConfig().env.creativePreviewBaseUrl ?? '', []);
	const [campaignId, setCampaignId] = useState('');
	const [campaignSelection, setCampaignSelection] = useState<string | null>(null);
	const [campaignOptions, setCampaignOptions] = useState<CampaignModel[]>([]);
	const [isCampaignsLoading, setIsCampaignsLoading] = useState(false);
	const [campaignsLoadMessage, setCampaignsLoadMessage] = useState('');
	const [campaignDestOptions, setCampaignDestOptions] = useState<CampaignDestinationLinkModel[]>([]);
	const [campaignDestUrl, setCampaignDestUrl] = useState('');
	const [campaignDestUrlId, setCampaignDestUrlId] = useState('');
	// const [destinationMap, setDestinationMap] = useState<Record<string, ICreative[]>>({});
	const [isCampaignDestLoading, setIsCampaignDestLoading] = useState(false);
	const [creativeId, setCreativeId] = useState('');
	const [creativeSelection, setCreativeSelection] = useState<string | null>(null);
	const [creativeOptions, setCreativeOptions] = useState<ICampaignCreativeLink[]>([]);
	const [campaignCreatives, setCampaignCreatives] = useState<CreativeModel[]>([]);
	const [isCreativesLoading, setIsCreativesLoading] = useState(false);
	const [creativeDesign, setCreativeDesign] = useState('');
	const [creativeBrand, setCreativeBrand] = useState('');
	const [creativeGroupId, setCreativeGroupId] = useState('');
	const [creativeDefaultDestUrl, setCreativeDefaultDestUrl] = useState('');
	const [creativeMetaStatus, setCreativeMetaStatus] = useState<CreativeMetaStatus>('hidden');
	const [width, setWidth] = useState('');
	const [height, setHeight] = useState('');
	const [campaignError, setCampaignError] = useState('');

	const previewUrl = useMemo(() => {
		if (!previewBase || !campaignId.trim()) {
			return null;
		}
		return buildPreviewUrl(previewBase, { campaignId, creativeId, width, height, campaignDestUrlId });
	}, [previewBase, campaignId, creativeId, width, height, campaignDestUrlId]);

	useEffect(() => {
		let cancelled = false;
		let retryTimeout: ReturnType<typeof setTimeout> | null = null;

		const loadCampaigns = (isRetry: boolean) => {
			setIsCampaignsLoading(true);
			if (isRetry) {
				setCampaignsLoadMessage('Retrying to load campaigns…');
			}

			CampaignsApi.list()
				.then(campaigns => {
					if (cancelled) {
						return;
					}
					setCampaignOptions(campaigns);
					setCampaignsLoadMessage('');
				})
				.catch(() => {
					if (cancelled) {
						return;
					}
					if (!isRetry) {
						setCampaignOptions([]);
						setCampaignsLoadMessage(
							'Something went wrong loading campaigns. Retrying automatically — this is usually resolved in a few seconds.'
						);
						retryTimeout = setTimeout(() => {
							if (cancelled) {
								return;
							}
							loadCampaigns(true);
						}, CAMPAIGNS_LOAD_RETRY_DELAY_MS);
					} else {
						setCampaignOptions([]);
						setCampaignsLoadMessage('Could not load campaigns. Please refresh the page or try again later.');
					}
				})
				.finally(() => {
					if (!cancelled) {
						setIsCampaignsLoading(false);
					}
				});
		};

		loadCampaigns(false);

		return () => {
			cancelled = true;
			if (retryTimeout) {
				clearTimeout(retryTimeout);
			}
		};
	}, []);

	useEffect(() => {
		const trimmed = campaignId.trim();
		if (!trimmed) {
			setCampaignCreatives([]);
			setCreativeOptions([]);
			setCreativeSelection(null);
			return;
		}

		let cancelled = false;
		setIsCreativesLoading(true);
		CreativesApi.listByCampaign(trimmed)
			.then(creatives => {
				if (cancelled) {
					return;
				}
				setCampaignCreatives(creatives);
				setCreativeOptions(creatives.map(c => ({ creative: c })));
			})
			.catch(() => {
				if (!cancelled) {
					setCampaignCreatives([]);
					setCreativeOptions([]);
				}
			})
			.finally(() => {
				if (!cancelled) {
					setIsCreativesLoading(false);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [campaignId]);

	useEffect(() => {
		const trimmed = campaignId.trim();
		if (!trimmed) {
			setCampaignDestOptions([]);
			return;
		}

		let cancelled = false;
		setIsCampaignDestLoading(true);
		CampaignsApi.get(trimmed)
			.then(campaign => {
				if (cancelled) {
					return;
				}
				const model = new CampaignModel(campaign);
				setCampaignDestOptions(model.getCampaignDestinations());
			})
			.catch(() => {
				if (!cancelled) {
					setCampaignDestUrl('');
				}
			})
			.finally(() => {
				if (!cancelled) {
					setIsCampaignDestLoading(false);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [campaignId]);

	const applyCreativeMeta = (creative: {
		brand?: string;
		default_dest_url?: string;
		creative_group?: { id?: string | number; design?: string };
	}) => {
		setCreativeDesign(creative.creative_group?.design?.trim() ?? '');
		setCreativeBrand(creative.brand?.trim() ?? '');
		setCreativeDefaultDestUrl(creative.default_dest_url?.trim() ?? '');
		setCreativeGroupId(creative.creative_group?.id?.toString() ?? '');
		setCreativeMetaStatus('resolved');
	};

	const clearCreativeMeta = () => {
		setCreativeDesign('');
		setCreativeBrand('');
		setCreativeDefaultDestUrl('');
		setCreativeGroupId('');
		setCreativeMetaStatus('hidden');
	};

	useEffect(() => {
		const trimmed = creativeId.trim();
		if (!trimmed) {
			clearCreativeMeta();
			return;
		}

		const fromList = campaignCreatives.find(c => String(c.id) === trimmed);
		if (fromList) {
			applyCreativeMeta(fromList);
			return;
		}

		let cancelled = false;
		clearCreativeMeta();
		setCreativeMetaStatus('loading');
		CreativesApi.get(trimmed)
			.then(creative => {
				if (cancelled) {
					return;
				}
				applyCreativeMeta(creative);
			})
			.catch(() => {
				if (!cancelled) {
					clearCreativeMeta();
					setCreativeMetaStatus('not_found');
				}
			});

		return () => {
			cancelled = true;
		};
	}, [creativeId, campaignCreatives]);

	const onSubmit = (e: FormEvent) => {
		e.preventDefault();
		if (!previewBase) {
			return;
		}
		if (!campaignId.trim()) {
			setCampaignError('Campaign is required');
			return;
		}
		setCampaignError('');
		const url = buildPreviewUrl(previewBase, { campaignId, creativeId, width, height, campaignDestUrlId });
		window.open(url, '_blank', 'noopener,noreferrer');
	};

	return (
		<Page>
			<div className={styles.wrapper}>
				<div className={styles.pageHeader}>
					<Pagebar>
						<PagebarTitle>Visualizer</PagebarTitle>
					</Pagebar>
				</div>
				{!previewBase ? (
					<p className={styles.hint}>
						Set <code className={styles.code}>REACT_APP_CREATIVE_PREVIEW_BASE_URL</code> in your environment (for example the
						origin and path prefix before <code className={styles.code}>creativePreview</code>, such as{' '}
						<code className={styles.code}>http://host/app</code>
						), then rebuild the app.
					</p>
				) : null}
				<Card className={styles.card}>
					<form className={styles.form} onSubmit={onSubmit}>
						<div className={styles.campaignBlock}>
							<div className={styles.formField}>
								<Dropdown
									label="Campaign *"
									placeholder="Select campaign"
									required
									requiredAsterisk={false}
									autocomplete
									autocompletePlaceholder="Search by name or campaign ID"
									isLoading={isCampaignsLoading}
									disabled={isCampaignsLoading || campaignOptions.length === 0}
									options={campaignOptions.map(c => `${c.id} - ${c.name}`)}
									defaultOption={campaignSelection ?? undefined}
									error={campaignError}
									wrapperClassName={styles.dropdownField}
									onSelect={val => {
										const label = firstFromSet(val);
										if (!label) {
											setCampaignSelection(null);
											setCampaignId('');
											setCreativeId('');
											setCreativeSelection(null);
											if (campaignError) {
												setCampaignError('');
											}
											return;
										}
										setCampaignSelection(label);
										setCampaignId(parseIdFromLabel(label) ?? '');
										setCreativeId('');
										setCreativeSelection(null);
										setCampaignDestUrl('');
										if (campaignError) {
											setCampaignError('');
										}
									}}
									isCloseIcon={true}
								/>
							</div>
							{campaignId.trim() ? (
								<div className={styles.formField}>
									<Dropdown
										label="Campaign destination (optional)"
										placeholder="Select campaign destination"
										autocomplete
										autocompletePlaceholder="Search by name or campaign destination ID"
										isLoading={isCampaignDestLoading}
										disabled={isCampaignDestLoading}
										options={campaignDestOptions.map(d => `${d.getCatalogDestinationId()} - ${d.getName()}`)}
										defaultOption={campaignDestUrl ?? undefined}
										onSelect={val => {
											const label = firstFromSet(val);
											if (!label) {
												setCampaignDestUrl('');
												setCampaignDestUrlId('');
												setCreativeId('');
												setCreativeOptions([]);
												setCreativeSelection(null);
											} else {
												setCampaignDestUrl(label);
												const destinationId = label.split(' - ')[0];
												const link = campaignDestOptions.find(
													d => String(d.getCatalogDestinationId()) === destinationId
												);
												const creatives = link?.getSelectedDestinationDto()?.creatives ?? [];
												setCampaignDestUrlId(String(link?.getCatalogDestinationId() ?? ''));
												setCreativeOptions(creatives);
												setCreativeId('');
												setCreativeSelection(null);
											}
										}}
									/>
								</div>
							) : null}
						</div>
						{campaignsLoadMessage ? <div className={styles.infoPlaceholder}>{campaignsLoadMessage}</div> : null}
						<div className={styles.formField}>
							<Input
								topLabel="Creative ID (optional)"
								value={creativeId}
								onValue={v => {
									setCreativeId(v);
									const pickedId = creativeSelection ? parseIdFromLabel(creativeSelection) : null;
									if (pickedId !== v.trim()) {
										setCreativeSelection(null);
									}
								}}
								isId={true}
							/>
						</div>
						{campaignId.trim() ? (
							<div className={styles.formField}>
								<Dropdown
									key={campaignDestUrl || 'all-destinations'}
									label="Pick from campaign (optional)"
									placeholder="Select creative"
									isLoading={isCreativesLoading}
									disabled={isCreativesLoading}
									options={creativeOptions.map(c => `${c.creative.id} - ${c.creative.brand}`)}
									defaultOption={creativeSelection ?? undefined}
									wrapperClassName={styles.dropdownField}
									onSelect={val => {
										const label = firstFromSet(val);
										if (!label) {
											setCreativeSelection(null);
											setCreativeId('');
											setCreativeOptions(campaignCreatives.map(c => ({ creative: c })));
											return;
										}
										setCreativeSelection(label);
										setCreativeId(parseIdFromLabel(label) ?? '');
									}}
									isCloseIcon={true}
								/>
							</div>
						) : null}
						{creativeId.trim() ? (
							<div className={styles.creativeMetaBlock}>
								<div className={styles.row}>
									<Input
										topLabel="Design"
										className={styles.readOnlyField}
										value={readOnlyFieldValue(
											creativeMetaStatus,
											creativeGroupId ? `${creativeGroupId} - ${creativeDesign}` : creativeDesign
										)}
										disabled={true}
									/>
									<Input
										topLabel="Brand"
										className={styles.readOnlyField}
										value={readOnlyFieldValue(creativeMetaStatus, creativeBrand, 'Creative not found')}
										disabled={true}
									/>
								</div>
								<div className={styles.formField}>
									<Input
										topLabel="Creative default destination URL"
										className={styles.readOnlyField}
										value={readOnlyFieldValue(creativeMetaStatus, creativeDefaultDestUrl)}
										disabled={true}
									/>
								</div>
							</div>
						) : null}
						<div className={styles.row}>
							<Input topLabel="Width (optional)" value={width} onValue={setWidth} type="number" />
							<Input topLabel="Height (optional)" value={height} onValue={setHeight} type="number" />
						</div>
						<div className={styles.previewUrlPanel}>
							<div className={styles.infoLabel}>Preview URL</div>
							{!previewBase ? (
								<div className={styles.infoPlaceholder}>
									Set <code className={styles.code}>REACT_APP_CREATIVE_PREVIEW_BASE_URL</code> to see the preview URL
								</div>
							) : !campaignId.trim() ? (
								<div className={styles.infoPlaceholder}>Select a campaign to see the preview URL</div>
							) : (
								<a
									className={styles.previewUrlText}
									href={previewUrl ?? undefined}
									target="_blank"
									rel="noopener noreferrer">
									{previewUrl}
								</a>
							)}
						</div>
						<div className={styles.buttonRow}>
							<PrimaryButton disabled={!previewBase}>Show Preview</PrimaryButton>
						</div>
					</form>
				</Card>
			</div>
		</Page>
	);
};
